import React, { useState } from 'react';
import { db, auth } from '../firebaseConfig';
import { doc, updateDoc } from 'firebase/firestore';
import { STokenLogo } from './StreamDisplay';
import './OnboardingModal.css';

const PLATFORMS = [
  { label: 'Instagram', icon: '📸', color: '#e1306c' },
  { label: 'TikTok',    icon: '🎵', color: '#69c9d0' },
  { label: 'YouTube',   icon: '▶️', color: '#ff0000' },
  { label: 'Facebook',  icon: '👍', color: '#1877f2' },
];

const SLIDES = [
  {
    id: 'intro',
    icon: '🤑',
    title: 'Get Paid to Watch',
    body: 'StreamStack pays you real Streams for watching content on Instagram, TikTok, YouTube, and Facebook — the platforms you\'re already on every day.',
    highlight: null,
  },
  {
    id: 'how',
    icon: '🔗',
    title: 'How It Works',
    body: 'You must use our in-app links to earn. Tap a platform below, watch content, then come back to StreamStack — we\'ll automatically credit your Streams.',
    platforms: true,
    steps: true,
    highlight: 'Opening a platform outside the app won\'t count. Always start your session from here.',
  },
  {
    id: 'forcequit',
    icon: '⚡',
    title: 'We\'ve Got You Covered',
    body: 'If your phone dies, the app force closes, or you get distracted — don\'t worry. The moment you reopen StreamStack, we calculate exactly how long you were watching and pay you out.',
    highlight: 'Your watch session is saved the instant you tap a platform. You\'ll never lose earned time.',
    forcequit: true,
  },
  {
    id: 'rate',
    icon: '🎯',
    title: '1 Stream Per Minute',
    body: 'Every minute of watch time earns you 1 Stream. They add up fast — an hour of scrolling earns 60 Streams, automatically credited when you return.',
    stat: true,
    highlight: null,
  },
  {
    id: 'payout',
    icon: '💸',
    title: 'Cash Out Anytime',
    body: 'Withdraw to Amazon Gift Cards, Google Play, Visa Prepaid, PayPal, or Crypto. Stack your Streams and cash out on your terms.',
    highlight: 'First payout: $5 minimum. After that: $10 per withdrawal.',
  },
];

export default function OnboardingModal({ onDone }) {
  const [step, setStep]       = useState(0);
  const [leaving, setLeaving] = useState(false);

  const isLast = step === SLIDES.length - 1;
  const slide  = SLIDES[step];

  const next = () => {
    if (isLast) finish();
    else setStep(s => s + 1);
  };

  const finish = async () => {
    setLeaving(true);
    try {
      const uid = auth.currentUser?.uid;
      if (uid) await updateDoc(doc(db, 'users', uid), { hasSeenOnboarding: true });
    } catch (_) {}
    setTimeout(onDone, 350);
  };

  return (
    <div className={`onboarding-overlay${leaving ? ' onboarding-overlay--out' : ''}`}>
      <div className={`onboarding-modal${leaving ? ' onboarding-modal--out' : ''}`}>

        {/* Progress dots */}
        <div className="onboarding__dots">
          {SLIDES.map((_, i) => (
            <span
              key={i}
              className={`onboarding__dot${i === step ? ' onboarding__dot--active' : ''}${i < step ? ' onboarding__dot--done' : ''}`}
            />
          ))}
        </div>

        {/* Slide content */}
        <div className="onboarding__slide" key={step}>
          <div className="onboarding__icon-wrap">
            <span className="onboarding__icon">{slide.icon}</span>
          </div>

          <h2 className="onboarding__title">{slide.title}</h2>
          <p className="onboarding__body">{slide.body}</p>

          {/* Platform grid — "How It Works" slide */}
          {slide.platforms && (
            <div className="onboarding__platforms">
              {PLATFORMS.map(p => (
                <div key={p.label} className="onboarding__platform-pill" style={{ '--p-color': p.color }}>
                  <span className="onboarding__platform-icon">{p.icon}</span>
                  <span className="onboarding__platform-label">{p.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Step flow — "How It Works" slide */}
          {slide.steps && (
            <div className="onboarding__steps">
              <div className="onboarding__step">
                <div className="onboarding__step-num">1</div>
                <div className="onboarding__step-text">Tap a platform in StreamStack</div>
              </div>
              <div className="onboarding__step-arrow">↓</div>
              <div className="onboarding__step">
                <div className="onboarding__step-num">2</div>
                <div className="onboarding__step-text">Watch content as long as you want</div>
              </div>
              <div className="onboarding__step-arrow">↓</div>
              <div className="onboarding__step">
                <div className="onboarding__step-num">3</div>
                <div className="onboarding__step-text">Come back — Streams are credited instantly</div>
              </div>
            </div>
          )}

          {/* Force quit illustration */}
          {slide.forcequit && (
            <div className="onboarding__forcequit-card">
              <div className="onboarding__forcequit-row">
                <span className="onboarding__forcequit-icon">📱</span>
                <div className="onboarding__forcequit-bar">
                  <div className="onboarding__forcequit-bar-fill" />
                </div>
                <span className="onboarding__forcequit-icon">✅</span>
              </div>
              <div className="onboarding__forcequit-label">
                App closed → Reopened → Streams credited
              </div>
            </div>
          )}

          {/* Stat card — rate slide */}
          {slide.stat && (
            <div className="onboarding__stat-card">
              <STokenLogo size={32} />
              <div>
                <div className="onboarding__stat-number">1 Stream</div>
                <div className="onboarding__stat-label">per minute of watch time</div>
              </div>
            </div>
          )}

          {slide.highlight && (
            <div className="onboarding__highlight">
              <span className="onboarding__highlight-icon">💡</span>
              <p className="onboarding__highlight-text">{slide.highlight}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="onboarding__actions">
          {step > 0 && (
            <button className="btn btn--ghost onboarding__back" onClick={() => setStep(s => s - 1)}>
              Back
            </button>
          )}
          <button className="btn btn--primary onboarding__next" onClick={next}>
            {isLast ? 'Get Started' : 'Next'}
          </button>
        </div>

        {!isLast && (
          <button className="onboarding__skip" onClick={finish}>Skip</button>
        )}
      </div>
    </div>
  );
}
